import { useEffect } from "react";
import * as Eden from "src/eden/content";
import { SprayChartGenerator } from "src/components/compounds/SprayChart/SprayChartGenerator";
import Styles from "./SprayChartModal.module.scss";

interface Props {
  playerName: string;
  sprayData: Eden.SprayChartData[];
  onClose: () => void;
}

/**
 * A small dismissable overlay showing one batter's spray chart, opened from a spray icon in the Box
 * Score. It renders on top of the (still-mounted) box score, so closing returns straight to it.
 * Reuses the existing {@link SprayChartGenerator} field, so it matches the player profile chart.
 */
export default function SprayChartModal({ playerName, sprayData, onClose }: Props) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const hitCount = sprayData.length;

  return (
    <div className={Styles.overlay} onClick={onClose} role="presentation">
      <div
        className={Styles.card}
        role="dialog"
        aria-modal="true"
        aria-label={`${playerName} spray chart`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={Styles.header}>
          <button type="button" className={Styles.close} onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
          <div className={Styles.titleBlock}>
            <div className={Styles.title}>{playerName}</div>
            <div className={Styles.subtitle}>
              Spray chart{hitCount ? ` · ${hitCount} ball${hitCount === 1 ? "" : "s"} in play` : ""}
            </div>
          </div>
        </div>
        <div className={Styles.body}>
          <SprayChartGenerator sprayChartData={sprayData} />
        </div>
      </div>
    </div>
  );
}
